import { Component, Input, Output, EventEmitter, inject, ChangeDetectionStrategy } from '@angular/core';

import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SchoolRosterStudent, UpdateSchoolRosterStudentRequest, ToastMessageType } from '@k12/models';
import { SchoolsService } from '@k12/school-services';
import { ToastMessageService } from '@k12/app-services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-graduation-actions',
  templateUrl: './graduation-actions.component.html',
  styleUrl: './graduation-actions.component.scss',
  standalone: true,
  imports: [MatButtonModule, MatProgressSpinnerModule]
})
export class GraduationActionsComponent {
  @Input() student: SchoolRosterStudent | null = null;
  @Input() form!: FormGroup;
  @Output() graduated = new EventEmitter<string>();
  @Output() cancelForm = new EventEmitter<void>();

  private readonly schoolsService = inject(SchoolsService);
  private readonly toastMessageService = inject(ToastMessageService);

  diplomaChoice: 'yes' | 'no' | null = null;
  submitting = false;

  onDiplomaChoiceSelect(choice: 'yes' | 'no'): void {
    this.diplomaChoice = choice;
  }

  onCancel(): void {
    this.diplomaChoice = null;
    this.cancelForm.emit();
  }

  onSubmit(): void {
    if (!this.student || !this.diplomaChoice || this.submitting) return;
    this.submitting = true;
    const dto = this.createDto();
    this.schoolsService.updateSchoolRosterStudent(dto).subscribe({
      next: (response) => {
        this.submitting = false;
        if (response?.success) {
          this.graduated.emit(this.student!.studentEnrollmentId);
          this.toastMessageService.showToast(ToastMessageType.Success, 'Graduation decision recorded');
        } else {
          this.toastMessageService.showToast(
            ToastMessageType.Error,
            response?.message || 'Failed to record graduation decision'
          );
        }
      },
      error: () => {
        this.submitting = false;
        this.toastMessageService.showToast(ToastMessageType.Error, 'Failed to record graduation decision');
      }
    });
  }

  private createDto(): UpdateSchoolRosterStudentRequest {
    if (!this.student) throw new Error('No student available');
    const now = new Date();
    const termCosts = (this.student.crossTermSummary || [])
      .filter((term) => term.studentEnrollmentId || new Date(term.endDate) >= now)
      .map((term) => ({
        studentEnrollmentId: term.studentEnrollmentId || null,
        schoolTermGradeId: term.schoolTermGradeId,
        termCost: term.payment
      }));

    return {
      studentEnrollmentId: this.student.studentEnrollmentId,
      studentId: this.student.studentId,
      schoolProgramPartnerId: this.student.schoolProgramPartnerId,
      schoolTermGradeId: this.student.schoolTermGradeId || '',
      attendanceTypeId: this.student.attendanceTypeId,
      rosterProgressStatusId: this.student.rosterProgressStatusId,
      academicYearStandardCostTotal: this.student.academicYearStandardCostTotal,
      academicYearIndividualCostTotal: this.student.academicYearIndividualCostTotal,
      termCost: (this.student.crossTermSummary || []).find(
        (t) => t.schoolTermGradeId === this.student!.schoolTermGradeId
      )?.payment,
      termCosts,
      certificationDate: this.student.certificationDate || null,
      endorsedDate: this.student.endorsedDate || null,
      startDate: this.student.startDate,
      endDate: this.student.termEndDate || null,
      hasHighSchoolDiploma: this.diplomaChoice === 'yes'
    };
  }

  get isSubmitDisabled(): boolean {
    return !this.diplomaChoice || this.submitting;
  }
}
